import {Component, computed, effect, inject, signal, Signal} from '@angular/core';
import {Button, ButtonIcon} from 'primeng/button';
import {OverlayBadge} from 'primeng/overlaybadge';
import {Avatar} from 'primeng/avatar';
import {Tooltip} from 'primeng/tooltip';
import {NavigationEnd, Router} from '@angular/router';
import {filter, map, startWith} from 'rxjs';
import {toSignal} from '@angular/core/rxjs-interop';
import Keycloak, {KeycloakUserInfo} from 'keycloak-js';
import {KEYCLOAK_EVENT_SIGNAL, KeycloakEventType} from 'keycloak-angular';
import {Skeleton} from 'primeng/skeleton';
import {SidebarService} from './service/sidebar.service';
import {SidebarMenuItem, SidebarSection} from './model/sidebar.model';

@Component({
  selector: 'app-sidebar',
  imports: [
    Button,
    ButtonIcon,
    OverlayBadge,
    Avatar,
    Tooltip,
    Skeleton
  ],
  templateUrl: './sidebar.component.html',
  styleUrl: './sidebar.component.css'
})
export class SidebarComponent {
  private readonly keycloak = inject(Keycloak);
  private readonly keycloakSignal = inject(KEYCLOAK_EVENT_SIGNAL);
  private readonly router = inject(Router);
  private readonly sidebarService = inject(SidebarService);

  protected readonly sidebarWrapped = signal(false);
  protected readonly sidebarWrapperDisabled = signal(false);
  protected readonly avatar = signal<string | null>(null);

  protected readonly sidebarSections = this.sidebarService.getSidebarSections();

  protected readonly currentRoute: Signal<string> = toSignal(
    this.router.events.pipe(
      filter((e): e is NavigationEnd => e instanceof NavigationEnd),
      map(() => this.router.url),
      startWith(this.router.url)
    ),
    {initialValue: this.router.url}
  );

  protected readonly activeMenuItems = computed<SidebarMenuItem[]>(() => {
    const url = this.currentRoute();
    const activeSection = this.sidebarSections.find(section =>
      url.startsWith(section.route)
    );
    return activeSection?.children ?? [];
  });

  constructor() {
    effect(() => {
      const event = this.keycloakSignal();
      if (event.type === KeycloakEventType.Ready) {
        this.keycloak.loadUserInfo().then(profile => this.resolveAvatar(profile));
      }
    });

    effect(() => {
      if (!this.activeMenuItems().length) {
        this.sidebarWrapped.set(true)
        this.sidebarWrapperDisabled.set(true)
      }
    });
  }

  private resolveAvatar(userInfo: KeycloakUserInfo): void {
    if (!userInfo) return;

    const firstNameLetter = userInfo['family_name']?.charAt(0)?.toUpperCase() || '?';
    const lastNameLetter = userInfo['given_name']?.charAt(0)?.toUpperCase() || '?';

    this.avatar.set(firstNameLetter + lastNameLetter);
  }

  protected isCurrentSection(section: SidebarSection): boolean {
    return this.currentRoute().startsWith(section.route);
  }

  protected isRouteActive(route: string): boolean {
    return this.currentRoute() === route || this.currentRoute().startsWith(route + '/');
  }

  protected toggleSidebar(): void {
    this.sidebarWrapped.update(v => !v);
  }

  protected navigateTo(item: SidebarMenuItem | SidebarSection): void {
    if ('children' in item && item.children.length > 0) {
      this.router.navigateByUrl(item.children[0].route);
    } else {
      this.router.navigateByUrl(item.route);
    }
  }

  protected onLogout(): void {
    this.keycloak.logout();
  }
}
